import React from 'react';
import getVideoId from 'get-video-id';
import PropTypes from 'prop-types';

import {
	IFrame as IFramePlayer,
	TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP,
} from '@techchange/components';

import IFrameMicrosoftPlayer from './IFrameMicrosoftPlayer';
import IFrameVimeoPlayer from './IFrameVimeoPlayer';
import IFrameYoutubePlayer from './IFrameYoutubePlayer';
import NativeVideoPlayer from './NativeVideoPlayer';

import './VideoFeed.scss';

const propTypes = {

	/**
	 * The video id.
	 */
	id: PropTypes.string,

	/**
	 * The video title.
	 */
	title: PropTypes.string,

	/**
	 * The video url.
	 * Required for the following services:
	 * Livestream
	 * Microsoft
	 * Cinemata
	 * YouTube
	 * Vimeo
	 * Twitch
	 * Other (Generic Embedded IFrame)
	 */
	src: PropTypes.string,

	/**
	 * The aspect ratio for the video. Defaults to 16x9.
	 */
	aspectRatio: PropTypes.string,

	/**
	 * Flag to auto play the video. Defaults to false.
	 */
	autoplay: PropTypes.bool,

	/**
	 * Function to handle video watching.
	 */
	onVideoWatch: PropTypes.func,

};

const defaultProps = {
	aspectRatio: TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP,
	autoplay: false,
};

/**
 * Returns a component that displays a video based on a provided URL or specific
 * input properties.
 *
 * Explicitly supported URLs/services include:
 * - Livestream
 * - Microsoft
 * - Cinemta
 * - YouTube
 * - Vimeo
 * - Twitch
 *
 * These will create a component using `react-player` or an explicit `iframe`
 * depending on the service. If the URL does not match the above list, a fallback
 * video component is created using `react-player`.
 *
 * @param {Object} props The component props.
 * @returns {Object}     The VideoFeed component with the relevant video player.
 */
const VideoFeed = (props) => {

	const {
		id,
		src,
		aspectRatio,
		title,
		autoplay,
		onVideoWatch
	} = props;

	if (!src) {
		return null;
	}

	// Check if the video to display is a Livestream.com embedded video
	if (src.indexOf('https://livestream.com') === 0) {

		const allow = 'autoplay; fullscreen; picture-in-picture';

		return (
			<IFramePlayer
				src={src}
				query={'enableInfoAndActivity=true&defaultDrawer=feed&autoPlay=true&mute=false'}
				allow={allow}
				aspectRatio={aspectRatio}
				title={title} />
		);

	}

	// Check if the video to display is a Microsoft embedded video
	if (src.indexOf('https://www.microsoft.com') === 0) {

		return (
			<IFrameMicrosoftPlayer
				id={id}
				src={src}
				aspectRatio={aspectRatio}
				title={title}
				onVideoWatch={onVideoWatch} />
		);

	}

	// Check if the video to display is a Cinemata embedded video
	if (src.indexOf('https://cinemata.org/embed') === 0) {

		const allow = 'autoplay; fullscreen; picture-in-picture';

		return (
			<IFramePlayer
				src={src}
				allow={allow}
				aspectRatio={aspectRatio}
				title={title} />
		);

	}

	// Check if the video to display is a Twitch embedded video
	if (src.indexOf('https://player.twitch.tv') === 0) {

		const allow = 'autoplay; fullscreen; picture-in-picture';

		return (
			<IFramePlayer
				src={src}
				allow={allow}
				query={`parent=${window.location.hostname}`}
				aspectRatio={aspectRatio}
				title={title} />
		);

	}

	// Otherwise, check if the video to display is a YouTube/Vimeo embedded video

	let videoInfo;

	if (src) {

		// Get the video's 3rd party id from the url
		videoInfo = getVideoId(src);

	}

	// Fall back if video info could not be fetched
	if (!videoInfo) {
		return <NativeVideoPlayer {...props} />;
	}

	// Get the 3rd party service and video id for the YouTube/Vimeo video
	const service = videoInfo.service;
	const videoId = videoInfo.id;

	if (service === 'youtube') {

		return (
			<IFrameYoutubePlayer
				id={id}
				youtubeVideoId={videoId}
				aspectRatio={aspectRatio}
				autoplay={autoplay}
				onVideoWatch={onVideoWatch} />
		);

	} else if (service === 'vimeo') {

		if (src.indexOf('https://vimeo.com/event') === 0) {
			// Allowed iframe attributes
			const allow = 'autoplay; fullscreen; picture-in-picture';

			return (
				<IFramePlayer
					src={src}
					allow={allow}
					aspectRatio={aspectRatio}
					title={title} />
			);
		}

		return (
			<IFrameVimeoPlayer
				id={id}
				src={src}
				aspectRatio={aspectRatio}
				autoplay={autoplay}
				onVideoWatch={onVideoWatch} />
		);

	}

	// Fall back for any other video type
	return <NativeVideoPlayer {...props} />;

};

VideoFeed.propTypes = propTypes;
VideoFeed.defaultProps = defaultProps;

export default VideoFeed;
